import { Button, Divider, Dropdown, Menu, Pagination, Space, Tooltip, Typography } from 'antd'
import { DownOutlined, SyncOutlined } from '@ant-design/icons'
import type { MenuProps } from 'antd'
import React, { useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import {
  DeleteModal,
  DraggingTableHeaders,
  FilterForm,
  IconFont,
  OptionType,
  SignTitle,
  SvgIcon,
  TableTag,
  FilterFormSelect,
} from '@/components'
import CreateModal from './create-modal'
import { useDeleteReport, useGetReportList, useWsUrl } from './utils'
import { GetReportListData } from '@/api/evaluation'
import { Message } from '@/components/message'
import { ShowTotal } from '@/components/show-total'
import { useSearchParams } from 'react-router-dom'
import { t } from '@/i18n'
const { Text } = Typography

interface PropsType {}

export interface DeleteReportModal {
  id?: string | string[]
  visible: boolean
}

type MenuItem = Required<MenuProps>['items'][number]

const getItem = (
  label: React.ReactNode,
  key?: React.Key | null,
  icon?: React.ReactNode,
  children?: MenuItem[],
  type?: 'group',
): MenuItem =>
  ({
    key,
    icon,
    children,
    label,
    type,
  } as MenuItem)

const ReportManagement: React.FC<{ id: string }> = ({ id }) => {
  const [getReportList] = useGetReportList()
  const [deleteTask, deleteTaskLoading] = useDeleteReport()
  const [wsUrl] = useWsUrl()

  let userData
  try {
    userData = JSON.parse(localStorage.getItem('token') as string)
  } catch (error) {}
  let [selectedRowKeys, setSelectedRowKeys] = useState<any>([])
  const [deleteTaskModal, setDeleteTaskModal] = useState<DeleteReportModal>({
    visible: false,
  })
  const [searchParams, setSearchParams] = useSearchParams()
  const [current, setCurrent] = useState(searchParams.get('tab') || id)
  const [visible, setVisible] = useState<boolean>(false)
  const [filterParam, setFilterParam] = useState<{ [k: string]: string | number | undefined }>()
  let [filterParams, setFilterParams] = useState<GetReportListData>({
    limit: 10,
    page: 1,
    name: '',
    status: undefined,
    start_time: '',
    end_time: '',
    campaign_type: 1,
  })
  const [filterValue, setFilterValue] = useState<{
    field: string | number
    value: string | number | string[] | number[] | undefined
  }>({
    field: '',
    value: '',
  })

  const [reportList, setReportList] = useState<any>([])
  const [total, setTotal] = useState<number>(0)

  const option = {
    name: {
      type: 'input',
      label: searchParams.get('type') === '10' ? t('报表名称/剧本名称') : t('报表名称/任务名称'),
    },
    status: {
      type: 'radio',
      label: t('报表状态'),
      data: [
        { name: t('生成中'), id: '1' },
        { name: t('已生成'), id: '2' },
        { name: t('异常'), id: '3' },
      ],
    },
    time: {
      type: 'time',
      label: t('创建时间'),
      apiParams: ['start_time', 'end_time'],
    },
  } as OptionType

  const filterOptions = [
    {
      label: id === '10' ? t('剧本名称') : t('任务名称'),
      value: 'task_name',
      type: 'input' as const,
    },
    {
      label: t('报表名称'),
      value: 'report_name',
      type: 'input' as const,
    },
    {
      label: t('报表状态'),
      value: 'status',
      type: 'select' as const,
      options: [
        { label: t('生成中'), value: '1' },
        { label: t('已生成'), value: '2' },
        { label: t('异常'), value: '3' },
      ],
    },
    {
      label: t('创建时间'),
      value: 'queryTime',
      type: 'dateRange' as const,
      placeholder: t('选择时间'),
    },
  ]

  // 下载报表
  const handleDownload = (item, p) => {
    const { id, report_name } = item
    let fileData: any[] = []
    console.log(
      '------------',
      JSON.stringify({
        id: id,
        user_id: userData.id,
        token: userData.token,
        template_type: p,
      }),
    )
    wsUrl()
      .then(res => {
        var ws = new WebSocket(`${res?.data?.ws_url}`)
        ws.onopen = function () {
          ws.send(
            JSON.stringify({
              id: id,
              user_id: userData.id,
              token: userData.token,
              template_type: p,
            }),
          )
        }
        ws.onmessage = function (e) {
          console.log(e.data, 'e.data')
          fileData.push(e.data)
        }
        ws.onerror = () => {
          ws.close()
        }
        ws.onclose = () => {
          console.log(fileData, 'fileData')
          if (fileData.length === 1 || fileData.length === 0) {
            Message.error(t('下载失败'))
            return
          }
          let filename = JSON.parse(fileData[0]).filename || ''
          fileData.shift()
          const blob = new Blob(fileData, {
            // type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            type: 'application/octet-stream',
          })
          const link = document.createElement('a')
          link.href = window.URL.createObjectURL(blob)
          link.download = filename ? `${filename}` : `${report_name}.${p == 2 ? 'xlsx' : 'html'}`
          link.click()
          window.URL.revokeObjectURL(link.href)
        }
      })
      .catch(_ => {})
  }

  // 生成下载菜单项
  const getDownloadMenuItems = (templateType, reportId, reportName, reportStatus) => {
    const formatMap = {
      2: 'Excel',
      3: 'Word',
      4: 'PDF',
    }

    return templateType
      ?.filter(p => p !== 1) // 过滤掉HTML格式
      ?.map(p => ({
        key: p,
        label: formatMap[p] || '',
        disabled: reportStatus === 1,
        onClick: () => {
          if (reportStatus !== 1) {
            handleDownload({ id: reportId, report_name: reportName }, p)
          }
        },
      }))
  }

  // 批量删除
  const deleteTaskHandler = async () => {
    try {
      await deleteTask({ id: deleteTaskModal.id, campaign_type: Number(current) })
      const res = await getReportList({ ...filterParams, campaign_type: Number(current) })
      setReportList(res.data)
      setTotal(res.data.total)
      setDeleteTaskModal({ visible: false })
      setSelectedRowKeys([])
    } catch (error) {}
  }

  const columns = useMemo(() => {
    if (current === '10') {
      return [
        {
          title: t('报表名称'),
          dataIndex: 'report_name',
          key: 'report_name',
          width: '10%',
          fixed: 'left',
          render: text => (
            <Typography.Text
              style={{ width: '100%' }}
              ellipsis={{ tooltip: { getPopupContainer: () => document.body } }}
            >
              {text}
            </Typography.Text>
          ),
        },
        {
          title: t('关联剧本运行记录'),
          key: 'associated_script',
          width: '20%',
          render: (_, { task_list }) => (
            <Text ellipsis>
              <Tooltip
                title={task_list?.map((item, index) => (
                  <span style={{ marginRight: '5px' }}>
                    {`${item?.task_name}(${item?.campaign_id})`}
                  </span>
                ))}
                getPopupContainer={() => document.body}
              >
                {<span>{`${task_list[0]?.task_name}(${task_list[0]?.campaign_id})`}</span>}
              </Tooltip>
            </Text>
          ),
        },
        {
          title: t('报表状态'),
          key: 'report_status',
          width: '10%',
          render: (_, { status, error_info }) => (
            <div>
              {' '}
              {status == 1 ? (
                <>
                  <TableTag color="#2973FF" background="#DBE8FF" borderRadius={4}>
                    {t('生成中')}
                  </TableTag>
                </>
              ) : status == 2 ? (
                <>
                  <TableTag color="#67C23A" background="#E4F7D1" borderRadius={4}>
                    {t('已生成')}
                  </TableTag>
                </>
              ) : (
                <Tooltip title={error_info} getPopupContainer={() => document.body}>
                  <TableTag color="#F53F3F" background="#FFDCE0" borderRadius={4}>
                    {t('异常')}
                  </TableTag>
                </Tooltip>
              )}
            </div>
          ),
        },
        {
          title: t('创建时间'),
          dataIndex: 'create_time',
          key: 'create_time',
          width: '15%',
        },
        {
          title: t('邮件发送'),
          key: 'email_send',
          width: '25%',
          render: (_, { email_status }) => (
            <>
              {email_status.length > 1 ? (
                <>
                  <span style={{ marginRight: '5px' }}>{email_status[0].email}</span>
                  <TableTag
                    color={email_status[0].notice_status === 1 ? '#67C23A' : '#ccc'}
                    background={email_status[0].notice_status === 1 ? '#E4F7D1' : '#777'}
                    borderRadius={4}
                  >
                    {email_status[0].notice_status === 1 ? t('已发送') : t('未发送')}
                  </TableTag>
                  <Tooltip
                    placement="top"
                    title={
                      <div style={{ width: 'auto' }}>
                        {email_status.length > 0 ? (
                          email_status.map((email, index) => (
                            <div style={{ marginBottom: '5px', display: 'flex' }} key={index}>
                              <span style={{ marginRight: '5px' }}>{email.email}</span>
                              <TableTag
                                color={email_status[0].notice_status === 1 ? '#67C23A' : '#ccc'}
                                background={
                                  email_status[0].notice_status === 1 ? '#E4F7D1' : '#777'
                                }
                                borderRadius={4}
                              >
                                {email_status[0].notice_status === 1 ? t('已发送') : t('未发送')}
                              </TableTag>
                            </div>
                          ))
                        ) : (
                          <span>-</span>
                        )}
                      </div>
                    }
                    getPopupContainer={() => document.body}
                  >
                    ...
                  </Tooltip>
                </>
              ) : email_status.length === 1 ? (
                <>
                  <Tooltip
                    placement="top"
                    title={
                      <div style={{ width: '200px' }}>
                        <div style={{ marginBottom: '5px', display: 'flex' }}>
                          <span style={{ marginRight: '5px', flexShrink: 0 }}>
                            {email_status[0].email}
                          </span>
                          <TableTag
                            color={email_status[0].notice_status === 1 ? '#67C23A' : '#ccc'}
                            background={email_status[0].notice_status === 1 ? '#E4F7D1' : '#777'}
                            borderRadius={4}
                          >
                            {email_status[0].notice_status === 1 ? t('已发送') : t('未发送')}
                          </TableTag>
                        </div>
                      </div>
                    }
                    getPopupContainer={() => document.body}
                  >
                    <span style={{ marginRight: '5px' }}>{email_status[0].email}</span>
                    <TableTag
                      color={email_status[0].notice_status === 1 ? '#67C23A' : '#ccc'}
                      background={email_status[0].notice_status === 1 ? '#E4F7D1' : '#777'}
                      borderRadius={4}
                    >
                      {email_status[0].notice_status === 1 ? t('已发送') : t('未发送')}
                    </TableTag>
                  </Tooltip>
                </>
              ) : (
                <span>-</span>
              )}
            </>
          ),
        },
        {
          title: t('操作'),
          key: 'action',
          width: '20%',
          fixed: 'right',
          render: (_, { template_type, status, id, report_name }) => {
            const downloadMenuItems = getDownloadMenuItems(template_type, id, report_name, status)
            const hasDownloadOptions = downloadMenuItems && downloadMenuItems.length > 0

            return (
              <div style={{ display: 'flex', alignItems: 'center' }}>
                {hasDownloadOptions && (
                  <>
                    <Dropdown
                      menu={{ items: downloadMenuItems }}
                      disabled={status === 1}
                      getPopupContainer={() => document.body}
                    >
                      <div
                        style={
                          status !== 1
                            ? {
                                cursor: 'pointer',
                                color: '#4238C9',
                                marginRight: '12px',
                                display: 'flex',
                                alignItems: 'center',
                              }
                            : {
                                cursor: 'not-allowed',
                                color: '#999',
                                marginRight: '12px',
                                display: 'flex',
                                alignItems: 'center',
                              }
                        }
                      >
                        {t('下载报表')}
                        <DownOutlined style={{ marginLeft: '4px', fontSize: '12px' }} />
                      </div>
                    </Dropdown>
                    <Divider type="vertical" />
                  </>
                )}
                <div
                  style={
                    status !== 1
                      ? {
                          display: 'flex',
                          alignItems: 'center',
                          cursor: 'pointer',
                          color: '#4238C9',
                          marginLeft: hasDownloadOptions ? '12px' : '0',
                        }
                      : {
                          display: 'flex',
                          alignItems: 'center',
                          cursor: 'not-allowed',
                          color: '#999',
                          marginLeft: hasDownloadOptions ? '12px' : '0',
                        }
                  }
                  onClick={() => {
                    if (status !== 1) {
                      setDeleteTaskModal({ visible: true, id: [id] })
                    }
                  }}
                >
                  <div className="operate" style={{ flexShrink: 0 }}>
                    {t('删除')}
                  </div>
                </div>
              </div>
            )
          },
        },
      ]
    } else if (current === '6') {
      return [
        {
          title: t('报表名称'),
          dataIndex: 'report_name',
          key: 'report_name',
          width: '30%',
          fixed: 'left',
          render: text => (
            <Typography.Text
              style={{ width: '100%' }}
              ellipsis={{ tooltip: { getPopupContainer: () => document.body } }}
            >
              {text}
            </Typography.Text>
          ),
        },
        {
          title: t('报表状态'),
          key: 'report_status',
          width: '10%',
          render: (_, { status, error_info }) => (
            <div>
              {' '}
              {status == 1 ? (
                <>
                  <TableTag color="#2973FF" background="#DBE8FF" borderRadius={4}>
                    {t('生成中')}
                  </TableTag>
                </>
              ) : status == 2 ? (
                <>
                  <TableTag color="#67C23A" background="#E4F7D1" borderRadius={4}>
                    {t('已生成')}
                  </TableTag>
                </>
              ) : (
                <Tooltip title={error_info} getPopupContainer={() => document.body}>
                  <TableTag color="#F53F3F" background="#FFDCE0" borderRadius={4}>
                    {t('异常')}
                  </TableTag>
                </Tooltip>
              )}
            </div>
          ),
        },
        {
          title: t('创建时间'),
          dataIndex: 'create_time',
          key: 'create_time',
          width: '15%',
        },
        {
          title: t('邮件发送'),
          key: 'email_send',
          width: '25%',
          render: (_, { email_status }) => (
            <>
              {email_status?.length > 1 ? (
                <>
                  <span style={{ marginRight: '5px' }}>{email_status[0].email}</span>
                  <TableTag
                    color={email_status[0].notice_status === 1 ? '#67C23A' : '#ccc'}
                    background={email_status[0].notice_status === 1 ? '#E4F7D1' : '#777'}
                    borderRadius={4}
                  >
                    {email_status[0].notice_status === 1 ? t('已发送') : t('未发送')}
                  </TableTag>
                  <Tooltip
                    placement="top"
                    getPopupContainer={() => document.body}
                    title={
                      <div style={{ width: 'auto' }}>
                        {email_status?.length > 0 ? (
                          email_status.map((email, index) => (
                            <div style={{ marginBottom: '5px', display: 'flex' }} key={index}>
                              <span style={{ marginRight: '5px' }}>{email.email}</span>
                              <TableTag
                                color={email_status[0].notice_status === 1 ? '#67C23A' : '#ccc'}
                                background={
                                  email_status[0].notice_status === 1 ? '#E4F7D1' : '#777'
                                }
                                borderRadius={4}
                              >
                                {email_status[0].notice_status === 1 ? t('已发送') : t('未发送')}
                              </TableTag>
                            </div>
                          ))
                        ) : (
                          <span>-</span>
                        )}
                      </div>
                    }
                  >
                    ...
                  </Tooltip>
                </>
              ) : email_status?.length === 1 ? (
                <>
                  <Tooltip
                    placement="top"
                    title={
                      <div style={{ width: '200px' }}>
                        <div style={{ marginBottom: '5px', display: 'flex' }}>
                          <span style={{ marginRight: '5px', flexShrink: 0 }}>
                            {email_status[0].email}
                          </span>
                          <TableTag
                            color={email_status[0].notice_status === 1 ? '#67C23A' : '#ccc'}
                            background={email_status[0].notice_status === 1 ? '#E4F7D1' : '#777'}
                            borderRadius={4}
                          >
                            {email_status[0].notice_status === 1 ? t('已发送') : t('未发送')}
                          </TableTag>
                        </div>
                      </div>
                    }
                    getPopupContainer={() => document.body}
                  >
                    <span style={{ marginRight: '5px' }}>{email_status[0].email}</span>
                    <TableTag
                      color={email_status[0].notice_status === 1 ? '#67C23A' : '#ccc'}
                      background={email_status[0].notice_status === 1 ? '#E4F7D1' : '#777'}
                      borderRadius={4}
                    >
                      {email_status[0].notice_status === 1 ? t('已发送') : t('未发送')}
                    </TableTag>
                  </Tooltip>
                </>
              ) : (
                <span>-</span>
              )}
            </>
          ),
        },
        {
          title: t('操作'),
          key: 'action',
          width: '20%',
          fixed: 'right',
          render: (_, { template_type, status, id, report_name }) => {
            const downloadMenuItems = getDownloadMenuItems(template_type, id, report_name, status)
            const hasDownloadOptions = downloadMenuItems && downloadMenuItems.length > 0

            return (
              <div style={{ display: 'flex', alignItems: 'center' }}>
                {hasDownloadOptions && (
                  <>
                    <Dropdown
                      menu={{ items: downloadMenuItems }}
                      disabled={status === 1}
                      getPopupContainer={() => document.body}
                    >
                      <div
                        style={
                          status !== 1
                            ? {
                                cursor: 'pointer',
                                color: '#4238C9',
                                marginRight: '12px',
                                display: 'flex',
                                alignItems: 'center',
                              }
                            : {
                                cursor: 'not-allowed',
                                color: '#999',
                                marginRight: '12px',
                                display: 'flex',
                                alignItems: 'center',
                              }
                        }
                      >
                        {t('下载报表')}
                        <DownOutlined style={{ marginLeft: '4px', fontSize: '12px' }} />
                      </div>
                    </Dropdown>
                    <Divider type="vertical" />
                  </>
                )}
                <div
                  style={
                    status !== 1
                      ? {
                          display: 'flex',
                          alignItems: 'center',
                          cursor: 'pointer',
                          color: '#4238C9',
                          marginLeft: hasDownloadOptions ? '12px' : '0',
                        }
                      : {
                          display: 'flex',
                          alignItems: 'center',
                          cursor: 'not-allowed',
                          color: '#999',
                          marginLeft: hasDownloadOptions ? '12px' : '0',
                        }
                  }
                  onClick={() => {
                    if (status !== 1) {
                      setDeleteTaskModal({ visible: true, id: [id] })
                    }
                  }}
                >
                  <div className="operate" style={{ flexShrink: 0 }}>
                    {t('删除')}
                  </div>
                </div>
              </div>
            )
          },
        },
      ]
    } else
      return [
        {
          title: t('报表名称'),
          dataIndex: 'report_name',
          key: 'report_name',
          width: '10%',
          fixed: 'left',
          render: text => (
            <Typography.Text
              style={{ width: '100%' }}
              ellipsis={{ tooltip: { getPopupContainer: () => document.body } }}
            >
              {text}
            </Typography.Text>
          ),
        },
        {
          title: t('关联任务'),
          dataIndex: 'task_list',
          key: 'task_list',
          width: '20%',
          ellispis: true,
          render: (_, { task_list }) => (
            <>
              {task_list?.length > 1 ? (
                <>
                  <Tooltip
                    title={
                      <div style={{ width: '200px' }}>
                        {task_list?.length > 0 ? (
                          task_list?.map((item, index) => (
                            <div
                              key={index}
                              style={index !== task_list.length - 1 ? { marginBottom: '5px' } : {}}
                            >
                              <span style={{ marginRight: '5px' }}>
                                {`${item?.task_name}(${item?.campaign_id})`}
                              </span>
                            </div>
                          ))
                        ) : (
                          <span>-</span>
                        )}
                      </div>
                    }
                    getPopupContainer={() => document.body}
                  >
                    <span>{`${task_list[0]?.task_name}(${task_list[0].campaign_id})`}</span>
                    <span>...</span>
                  </Tooltip>
                </>
              ) : task_list?.length === 1 ? (
                <Typography.Text
                  ellipsis={{ tooltip: { getPopupContainer: () => document.body } }}
                  style={{ width: '100%' }}
                >
                  {task_list[0]?.task_name}({task_list[0]?.campaign_id})
                </Typography.Text>
              ) : (
                '-'
              )}
            </>
          ),
        },
        {
          title: t('报表状态'),
          key: 'report_status',
          width: '10%',
          render: (_, { status, error_info }) => (
            <div>
              {' '}
              {status == 1 ? (
                <>
                  <TableTag color="#2973FF" background="#DBE8FF" borderRadius={4}>
                    {t('生成中')}
                  </TableTag>
                </>
              ) : status == 2 ? (
                <>
                  <TableTag color="#67C23A" background="#E4F7D1" borderRadius={4}>
                    {t('已生成')}
                  </TableTag>
                </>
              ) : (
                <Tooltip title={error_info} getPopupContainer={() => document.body}>
                  <TableTag color="#F53F3F" background="#FFDCE0" borderRadius={4}>
                    {t('异常')}
                  </TableTag>
                </Tooltip>
              )}
            </div>
          ),
        },
        {
          title: t('创建时间'),
          dataIndex: 'create_time',
          key: 'create_time',
          width: '15%',
        },
        {
          title: t('邮件发送'),
          key: 'email_send',
          width: '25%',
          render: (_, { email_status }) => (
            <>
              {email_status?.length > 1 ? (
                <>
                  <span style={{ marginRight: '5px' }}>{email_status[0].email}</span>
                  <TableTag
                    color={email_status[0].notice_status === 1 ? '#67C23A' : '#ccc'}
                    background={email_status[0].notice_status === 1 ? '#E4F7D1' : '#777'}
                    borderRadius={4}
                  >
                    {email_status[0].notice_status === 1 ? t('已发送') : t('未发送')}
                  </TableTag>
                  <Tooltip
                    placement="top"
                    title={
                      <div style={{ width: 'auto' }}>
                        {email_status.length > 0 ? (
                          email_status.map((email, index) => (
                            <div style={{ marginBottom: '5px', display: 'flex' }} key={index}>
                              <span style={{ marginRight: '5px' }}>{email.email}</span>
                              <TableTag
                                color={email.notice_status === 1 ? '#67C23A' : '#ccc'}
                                background={email.notice_status === 1 ? '#E4F7D1' : '#777'}
                                borderRadius={4}
                              >
                                {email.notice_status === 1 ? t('已发送') : t('未发送')}
                              </TableTag>
                            </div>
                          ))
                        ) : (
                          <span>-</span>
                        )}
                      </div>
                    }
                    getPopupContainer={() => document.body}
                  >
                    ...
                  </Tooltip>
                </>
              ) : email_status?.length === 1 ? (
                <>
                  <Tooltip
                    placement="top"
                    title={
                      <div style={{ width: '200px' }}>
                        <div style={{ marginBottom: '5px', display: 'flex' }}>
                          <span style={{ marginRight: '5px', flexShrink: 0 }}>
                            {email_status[0].email}
                          </span>
                          <TableTag
                            color={email_status[0].notice_status === 1 ? '#67C23A' : '#ccc'}
                            background={email_status[0].notice_status === 1 ? '#E4F7D1' : '#777'}
                            borderRadius={4}
                          >
                            {email_status[0].notice_status === 1 ? t('已发送') : t('未发送')}
                          </TableTag>
                        </div>
                      </div>
                    }
                    getPopupContainer={() => document.body}
                  >
                    <span style={{ marginRight: '5px' }}>{email_status[0].email}</span>
                    <TableTag
                      color={email_status[0].notice_status === 1 ? '#67C23A' : '#ccc'}
                      background={email_status[0].notice_status === 1 ? '#E4F7D1' : '#777'}
                      borderRadius={4}
                    >
                      {email_status[0].notice_status === 1 ? t('已发送') : t('未发送')}
                    </TableTag>
                  </Tooltip>
                </>
              ) : (
                <span>-</span>
              )}
            </>
          ),
        },
        {
          title: t('操作'),
          key: 'action',
          width: '20%',
          fixed: 'right',
          render: (_, { template_type, status, id, report_name }) => {
            const downloadMenuItems = getDownloadMenuItems(template_type, id, report_name, status)
            const hasDownloadOptions = downloadMenuItems && downloadMenuItems.length > 0

            return (
              <div style={{ display: 'flex', alignItems: 'center' }}>
                {hasDownloadOptions && (
                  <>
                    <Dropdown
                      menu={{ items: downloadMenuItems }}
                      disabled={status === 1}
                      getPopupContainer={() => document.body}
                    >
                      <div
                        style={
                          status !== 1
                            ? {
                                cursor: 'pointer',
                                color: '#4238C9',
                                marginRight: '12px',
                                display: 'flex',
                                alignItems: 'center',
                              }
                            : {
                                cursor: 'not-allowed',
                                color: '#999',
                                marginRight: '12px',
                                display: 'flex',
                                alignItems: 'center',
                              }
                        }
                      >
                        {t('下载报表')}
                        <DownOutlined style={{ marginLeft: '4px', fontSize: '12px' }} />
                      </div>
                    </Dropdown>
                    <Divider type="vertical" />
                  </>
                )}
                <div
                  style={
                    status !== 1
                      ? {
                          display: 'flex',
                          alignItems: 'center',
                          cursor: 'pointer',
                          color: '#4238C9',
                          marginLeft: hasDownloadOptions ? '12px' : '0',
                        }
                      : {
                          display: 'flex',
                          alignItems: 'center',
                          cursor: 'not-allowed',
                          color: '#999',
                          marginLeft: hasDownloadOptions ? '12px' : '0',
                        }
                  }
                  onClick={() => {
                    if (status !== 1) {
                      setDeleteTaskModal({ visible: true, id: [id] })
                    }
                  }}
                >
                  <div className="operate" style={{ flexShrink: 0 }}>
                    {t('删除')}
                  </div>
                </div>
              </div>
            )
          },
        },
      ]
  }, [current])

  const fetchList = () => {
    if (!current) return
    getReportList({ ...filterParams, campaign_type: Number(current) })
      .then(res => {
        setReportList(res.data)
        setTotal(res.data.total)
      })
      .catch(_ => {})
  }

  useEffect(() => {
    if (filterValue?.field) {
      const selectedOption = filterOptions.find(opt => opt.value === filterValue.field)
      if (selectedOption) {
        setFilterParams(prev => {
          // 处理 dateRange 类型
          if (selectedOption.type === 'dateRange' && Array.isArray(filterValue.value)) {
            // 如果时间范围为空数组，清除时间参数和其他筛选字段
            if (filterValue.value.length === 0) {
              // eslint-disable-next-line @typescript-eslint/no-unused-vars
              const {
                start_time: startTime,
                end_time: endTime,
                name,
                taskName,
                status,
                ...rest
              } = prev
              return {
                ...rest,
                page: 1,
                limit: prev.limit,
                campaign_type: prev.campaign_type,
              }
            }
            // 如果有两个时间值，设置 start_time 和 end_time，并清除其他筛选字段
            if (filterValue.value.length === 2) {
              // eslint-disable-next-line @typescript-eslint/no-unused-vars
              const { name, taskName, status, ...rest } = prev
              return {
                ...rest,
                page: 1,
                start_time: filterValue.value[0] as string,
                end_time: filterValue.value[1] as string,
              }
            }
            return prev
          }

          // 处理其他类型：清除时间参数
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { start_time: startTime, end_time: endTime, ...restParams } = prev

          // 检查值是否变化
          if (restParams[filterValue.field] === filterValue.value) {
            return prev
          }

          // 如果值为空字符串或空数组，说明是切换了字段，需要清除所有筛选字段
          if (
            filterValue.value === '' ||
            (Array.isArray(filterValue.value) && filterValue.value.length === 0)
          ) {
            return {
              page: 1,
              limit: prev.limit,
              campaign_type: prev.campaign_type,
            }
          }

          // 设置新的筛选值，清除其他筛选字段
          return {
            page: 1,
            limit: prev.limit,
            campaign_type: prev.campaign_type,
            [filterValue.field]: filterValue.value,
          }
        })
      }
    }
  }, [filterValue])

  useEffect(() => {
    fetchList()
  }, [filterParams, current])

  // 监听URL参数tab的变化
  useEffect(() => {
    const tabParam = searchParams.get('tab')
    if (tabParam && tabParam !== current) {
      setCurrent(tabParam)
    }
  }, [searchParams])

  const rowSelection = {
    preserveSelectedRowKeys: true,
    selectedRowKeys,
    onSelectAll: selected => {
      setSelectedRowKeys(selected ? reportList.items.map(item => item.id) : [])
    },
    onSelect: (record, selected) => {
      let arr = [...selectedRowKeys] as string[]
      if (selected) {
        arr.push(record.id)
      } else {
        arr = arr.filter(item => item !== record.id)
      }
      setSelectedRowKeys(arr)
    },
    getCheckboxProps: record => ({
      disabled: record.status === 1,
    }),
    renderCell: (checked, record, index, originNode) => {
      if (record.id === '0') {
        return null
      }
      return originNode
    },
  }

  return (
    <Container>
      <Content>
        <div className="top">
          <span>{t('报表列表')}</span>
          <span style={{ display: 'flex', alignItems: 'center' }}>
            <FilterFormSelect
              filterOptions={filterOptions}
              value={filterValue}
              onChange={newValue => {
                setFilterValue(newValue)
              }}
            />

            <Button
              type="primary"
              style={{ color: '#fff', margin: '0 8px' }}
              onClick={() => setVisible(true)}
            >
              {t('创建报表')}
            </Button>
            <Button
              icon={<SyncOutlined />}
              className="reset-btn"
              onClick={() => {
                setFilterParams({ ...filterParams, page: 1, limit: 10 })
              }}
            />
          </span>
        </div>
        {/* <FilterForm
           row={3}
           onReset={() => {
             setFilterParams({ page: 1, limit: 10 })
           }}
           option={option}
           setSearchParams={setFilterParams}
           setFilterParams={setFilterParam}
           filterParams={filterParam}
           isAside
          /> */}
        <div style={{ padding: '0 20px' }}>
          {columns.length > 0 && (
            <DraggingTableHeaders
              key={current}
              scroll={{ y: 'auto' }}
              rowSelection={rowSelection}
              columns={columns}
              dataSource={reportList?.items}
              selectedRowKeys={selectedRowKeys as string[]}
              setSelectedRowKeys={setSelectedRowKeys}
              pagination={false}
              batchOperation={
                <Space
                  split={<Divider type={'vertical'} />}
                  onClick={e => {
                    e.stopPropagation()
                  }}
                >
                  <div>{t('批量操作')}</div>
                  <Delete
                    active={selectedRowKeys.length > 0}
                    onClick={() =>
                      selectedRowKeys.length > 0 &&
                      setDeleteTaskModal({ visible: true, id: selectedRowKeys })
                    }
                  >
                    <SvgIcon name={'batch_remove'} className="icon-delete" />
                    {t('批量删除')}
                  </Delete>
                </Space>
              }
            />
          )}
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '10px 20px' }}>
          <Pagination
            current={filterParams.page}
            pageSize={filterParams.limit}
            pageSizeOptions={['10', '20', '50', '100']}
            total={total}
            showTotal={totalCount => <ShowTotal>{totalCount}</ShowTotal>}
            showSizeChanger
            showQuickJumper
            onChange={(page, pageSize) => {
              setFilterParams({ ...filterParams, page: page, limit: pageSize })
            }}
          />
        </div>
      </Content>
      <CreateModal
        scene={current}
        title={<SignTitle show>{t('创建报表')}</SignTitle>}
        open={visible}
        updateList={fetchList}
        onCancel={() => setVisible(false)}
      />

      <DeleteModal
        onOk={deleteTaskHandler}
        visible={deleteTaskModal.visible}
        title={<SignTitle show>{t('提示')}</SignTitle>}
        confirmLoading={deleteTaskLoading}
        onCancel={() => {
          setDeleteTaskModal({ visible: false })
        }}
      />
    </Container>
  )
}

export default ReportManagement

const Container = styled.div`
  height: 100%;
  background: #f1f3f5;
  padding: 16px;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;

  .ant-menu {
    background: #ffffff;
    box-shadow: 0px 4px 8px 0px rgba(95, 103, 129, 0.06);
    border-radius: 8px;
    padding: 0 6px;

    .ant-menu-submenu-title:active {
      background-color: #ebeef7;
    }

    .ant-menu-item:hover {
      background: #ebeef7;
      border-radius: 6px;
    }

    .ant-menu-item::after {
      content: none;
    }

    .ant-menu-item-selected {
      background: #ebeef7;
      border-radius: 6px;
    }
  }
`

const Icon = styled(IconFont)`
  color: #4238c9;
  cursor: pointer;
  margin-right: 3px;
`

const Content = styled.div`
  width: 100%;
  height: 100%;
  background: #ffffff;
  box-shadow: 0px 4px 8px 0px rgba(95, 103, 129, 0.06);
  border-radius: 8px;
  padding: 12px 0px;
  overflow-y: scroll;

  .top {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0 20px;

    > span {
      font-weight: 500;
      font-size: 16px;
      color: #333333;
    }
  }

  .ant-tooltip .ant-tooltip-content {
    width: 210px !important;
  }
`

interface DeleteItem {
  active: boolean
}

const Delete = styled.div<DeleteItem>`
  width: 100px;
  height: 30px;
  line-height: 32px;
  font-size: 14px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  cursor: ${props => (props.active ? 'pointer' : 'no-drop')};
  color: ${props => (props.active ? '#333' : '#999')};
  justify-content: center;
  background: #f2f3f5;
  transition: 0.3s;
  &:hover {
    background: ${props => (props.active ? 'rgba(245, 63, 63, 0.1)' : '')};
    .icon-delete {
      width: 12px;
      stroke: ${props => (props.active ? '#F53F3F' : '#999')};
    }
  }
  .icon-delete {
    width: 12px;
    stroke: ${props => (props.active ? '#F53F3F' : '#999')};
    fill: ${props => (props.active ? '#F53F3F' : '#999')};
    transition: 0.3s;
    height: 12px;
    margin-right: 3px;
  }
`
